/**
 * Walks a paginated Event Hub list — ListNominationContacts, ListEventAttendees — handing over
 * everything loaded so far as each page arrives.
 *
 * Both operations return at most 100 items per call (ListNominationContacts used to read every
 * Customer Link page first, and a BD with ~7,000 contacts hit API Gateway's 29 s timeout). The
 * page shows the first page at once and keeps loading the rest behind it, so a list or a search
 * grows rather than waiting on the whole set — and still ends up covering all of it, since
 * neither operation has a search of its own to fall back on.
 */
import type { Contact } from '../client/EventCalendarClient';

/** One page of a paginated list. */
export interface Page<T> {
  items: T[];
  nextToken?: string;
}

/**
 * Fetches page after page until the service stops returning a `nextToken`, calling `onPage` with
 * everything loaded so far after each one. Stops quietly when `signal` aborts.
 *
 * `maxPages` bounds the walk, so a service that kept handing out tokens could not keep the page
 * fetching forever. Resolves with the full list and whether it was cut short.
 */
export async function loadAllPages<T>(
  fetchPage: (nextToken: string | undefined) => Promise<Page<T>>,
  onPage: (loaded: readonly T[], done: boolean) => void,
  signal?: AbortSignal,
  maxPages = 200,
): Promise<{ items: T[]; truncated: boolean }> {
  const loaded: T[] = [];
  let nextToken: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const result = await fetchPage(nextToken);
    if (signal?.aborted) return { items: loaded, truncated: true };
    loaded.push(...result.items);
    nextToken = result.nextToken;
    onPage([...loaded], !nextToken);
    if (!nextToken) return { items: loaded, truncated: false };
  }
  return { items: loaded, truncated: true };
}

export type ContactPageFetcher = (input: {
  signal?: AbortSignal;
  nextToken?: string;
}) => Promise<{ contacts: Contact[]; nextToken?: string }>;

/** {@link loadAllPages} over ListNominationContacts. */
export async function loadContactPages(
  fetchPage: ContactPageFetcher,
  onPage: (loaded: readonly Contact[], done: boolean) => void,
  signal?: AbortSignal,
  maxPages = 200,
): Promise<{ contacts: Contact[]; truncated: boolean }> {
  const { items, truncated } = await loadAllPages(
    async (nextToken) => {
      const page = await fetchPage({ signal, nextToken });
      return { items: page.contacts, nextToken: page.nextToken };
    },
    onPage,
    signal,
    maxPages,
  );
  return { contacts: items, truncated };
}
